/**
 * SPA.3 "spa-cache" block — the server-state cache. LLD-SPA-061: every key is built by one
 * function that requires organisation and region; LLD-SPA-063: policy is a table, not a per-call
 * argument; LLD-SPA-065: invalidation is named per mutation, never "refetch everything."
 *
 * a-confirm calls no query (it is unauthenticated and pre-organisation, so there is no [orgId,
 * region] pair to key against — its two calls are both mutations), so this file's cache-key
 * builder and policy table are exercised by nothing in this pass's one proof screen. They are
 * still built for real, to the LLD-SPA-061/063 shape, rather than stubbed, because a shape this
 * load-bearing should not be improvised the first time a query screen needs it.
 */
import { QueryClient } from '@tanstack/react-query'

export type Region = 'home' | string

export type ResourceKind = 'reference' | 'jobState' | 'content'

export type CacheKey = readonly [resource: string, orgId: string, region: Region, ...rest: unknown[]]

/** LLD-SPA-061: "Every key is constructed by one builder that requires both [org and region]." */
export function buildKey(resource: string, orgId: string, region: Region, ...rest: unknown[]): CacheKey {
  return [resource, orgId, region, ...rest]
}

interface CachePolicy {
  readonly staleMs: number
  readonly gcMs: number
  readonly evictOn: readonly ('signOut' | 'workspaceChange' | 'viewUnmount')[]
}

/** LLD-SPA-063: "Reference data is cached long; job state is short-lived and refetched." Values
 * are this pass's own read of §24.2 rule 26 — no other file states a number, so these three are a
 * finding to confirm, not a value taken as settled. */
export const POLICY: Record<ResourceKind, CachePolicy> = {
  reference: { staleMs: 10 * 60_000, gcMs: 30 * 60_000, evictOn: ['signOut'] },
  jobState: { staleMs: 5_000, gcMs: 60_000, evictOn: ['workspaceChange', 'signOut'] },
  content: { staleMs: 0, gcMs: 0, evictOn: ['viewUnmount', 'workspaceChange', 'signOut'] },
}

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: POLICY.reference.staleMs,
        gcTime: POLICY.reference.gcMs,
        retry: false,
      },
      mutations: {
        retry: false,
      },
    },
  })
}
